import React, { useEffect, useState } from "react";
import { useUser } from "contexts/UserContext";
import { Button } from "react-bootstrap";
import { AmplitudePageName, track } from "utils/amplitude";
import createOrUpdateConsentSetting from "services/patient-consent/createOrUpdateConsentSetting";
import queryConsentSetting from "services/patient-consent/queryConsentSetting";
import RadioButton from "components/radio-button";
import SuccessMessage from "components/success-message";
import ErrorMessage from "components/error-message";
import styles from "./PracticeSetting.module.scss";

const CONSENT_OPTIONS = Object.freeze({
  default: {
    value: "default",
    stringId: "Opt for Orchid's privacy and consent handling",
  },
  custom: {
    value: "custom",
    stringId: "Use my own privacy policy and consent management",
  },
});

const ConsentCollection = ({
  AMPLITUDE_PAGE_NAME,
}: {
  AMPLITUDE_PAGE_NAME: AmplitudePageName;
}) => {
  const { user } = useUser();

  useEffect(() => {
    if (!user) return;

    queryConsentSetting().then((res) => {
      const { use_orchid_default } = res;
      if (use_orchid_default) {
        setConsentOption("default");
      } else {
        setConsentOption("custom");
      }
    });
  }, [user]);

  const [consentOption, setConsentOption] = useState<string>("default");
  const [changed, setChanged] = useState<boolean>();
  const [success, setSuccess] = useState<string>();
  const [error, setError] = useState<any>();

  const onConsentOptionChange = (event) => {
    setChanged(true);
    const inputField = event.currentTarget;
    setConsentOption(inputField.value);
  };

  const submitConsentOption = () => {
    try {
      createOrUpdateConsentSetting(consentOption === "default" ? true : false);
      setChanged(false);
      setSuccess("Saved");
    } catch (error) {
      setError(error);
    }
  };

  return (
    <>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error?.toString()}
          className={styles["data-analysis__error-message"]}
        />
      )}
      {success && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccess(undefined)}
          message={"Saved"}
          duration={1000}
        />
      )}
      <div className={styles["practice-setting__body"]}>
        <div className={styles["practice-setting__section"]}>
          <div className={styles["practice-setting__section-title"]}>
            Consent Collection
          </div>
          <div className={styles["practice-setting__content"]}>
            {Object.entries(CONSENT_OPTIONS).map(([key, value]) => (
              <RadioButton
                key={key}
                className={styles["practice-setting__radio"]}
                id={value.value}
                label={value.stringId}
                value={value.value}
                name="consent-option"
                onChange={onConsentOptionChange}
                checked={consentOption === value.value}
                onClick={() =>
                  track(
                    AMPLITUDE_PAGE_NAME,
                    "consent-option-radio-button",
                    "click",
                  )
                }
              />
            ))}
          </div>
        </div>
      </div>
      <div className={styles["practice-setting__footer"]}>
        <div className={"gray01 rg-12"}>
          ©2024 Orchid Exchange Inc. All rights reserved
        </div>
        <Button
          disabled={!changed}
          variant="primary"
          className={styles["practice-setting__save-button"]}
          onClick={() => {
            submitConsentOption();
            track(AMPLITUDE_PAGE_NAME, "save-button", "click");
          }}
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default ConsentCollection;
